import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { zhCN, enGB } from "date-fns/locale";
import { billingService } from "../services/billing.service";
import { accountService } from "../services/account.service";
import { tagOf } from "../utils/api-error";
import ProfileSettings from "../components/ProfileSettings";
import { useSubscription } from "../hooks/useSubscription";
import { Plan, PendingPlanChange } from "../interfaces/billing.interface";
import { formatAmount } from "../utils/money";
import AddonStepper from "../components/AddonStepper";
import ChangePlan from "../components/ChangePlan";

// Tags the billing endpoints on this page document (portal-session and
// resume, docs/web-api-spec.md). Anything else is a server-side surprise and
// gets the one generic message.
const KNOWN_TAGS = [
  "no_customer",
  "stripe_unavailable",
  "no_subscription",
  "card_declined",
  "authentication_failed",
  "addon_quantity_invalid",
  "addons_not_sold",
  "addon_price_missing",
];

// `tier` is a DB column on subscription_plans, so the value set is
// open-ended. Only translate the tiers we know about today; a tier added to
// the DB later still renders, just untranslated, instead of a raw t() key.
const KNOWN_TIERS = ["free", "pro", "elite", "ultimate"];

const Account = () => {
  const { t, i18n } = useTranslation(["billing", "common"]);
  const { data, loading, error, reload } = useSubscription();
  const [portalTag, setPortalTag] = useState<string | null>(null);
  const [portalPending, setPortalPending] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteFailed, setDeleteFailed] = useState(false);
  const [pendingChange, setPendingChange] = useState<PendingPlanChange | null>(
    null,
  );
  const [resumed, setResumed] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [resumeTag, setResumeTag] = useState<string | null>(null);
  // The current plan row: its includedSubjects and add-on price table. Only a
  // self-paid subscriber can change add-ons, so only they fetch it.
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [addonTarget, setAddonTarget] = useState<number | null>(null);
  const [addonConfirm, setAddonConfirm] = useState<
    | { kind: "increase"; amountDue: number; currency: string }
    | { kind: "decrease" }
    | { kind: "keep" }
    | null
  >(null);
  const [addonBusy, setAddonBusy] = useState(false);
  const [addonTag, setAddonTag] = useState<string | null>(null);
  const [addonDone, setAddonDone] = useState<"increase" | "decrease" | "keep" | null>(
    null,
  );
  const lang = i18n.resolvedLanguage ?? "en";

  useEffect(() => {
    if (data && data.source === "self" && data.tier !== "free") {
      void billingService
        .getPlans(lang)
        .then(setPlans)
        .catch(() => setPlans(null));
    }
  }, [data, lang]);

  const fmtDate = (iso: string) =>
    format(new Date(iso), "d MMM yyyy", {
      locale: i18n.resolvedLanguage === "zh" ? zhCN : enGB,
    });

  const onReviewAddons = async (target: number, current: number) => {
    setAddonTag(null);
    setAddonDone(null);
    if (target <= current) {
      // Equal is only reachable with a reduction pending: keep the current
      // count, which cancels it. Nothing is charged, so no preview.
      setAddonConfirm({ kind: target < current ? "decrease" : "keep" });
      return;
    }
    setAddonBusy(true);
    try {
      const preview = await billingService.previewAddons(target);
      setAddonConfirm({ kind: "increase", ...preview });
    } catch (err) {
      setAddonTag(tagOf(err) ?? "generic");
    } finally {
      setAddonBusy(false);
    }
  };

  const onConfirmAddons = async (target: number) => {
    if (!addonConfirm) return;
    setAddonBusy(true);
    try {
      await billingService.updateAddons(target);
      setAddonDone(addonConfirm.kind);
      setAddonConfirm(null);
      setAddonTarget(null);
      // An increase lands on the webhook; a decrease re-reads pending-change
      // through the data effect once the reload resolves.
      void reload();
    } catch (err) {
      setAddonTag(tagOf(err) ?? "generic");
    } finally {
      setAddonBusy(false);
    }
  };

  // A scheduled downgrade only exists for a self-paid, non-free subscriber;
  // the endpoint returns null for anyone else, but there is no reason to make
  // the round-trip for them. Read live from Stripe, so a failure is swallowed
  // (the service resolves null) and the banner simply doesn't render.
  useEffect(() => {
    if (data && data.source === "self" && data.tier !== "free") {
      void billingService
        .getPendingChange()
        .then(setPendingChange)
        .catch(() => setPendingChange(null));
    }
  }, [data]);

  const onResume = async () => {
    setResumeTag(null);
    setResuming(true);
    try {
      await billingService.resume();
      // The cancellation is undone at Stripe immediately; the DB catches up on
      // the webhook. Flip the view now rather than flash a stale "Cancels on".
      setResumed(true);
      void reload();
    } catch (err) {
      setResumeTag(tagOf(err) ?? "generic");
    } finally {
      setResuming(false);
    }
  };

  const onManageBilling = async () => {
    setPortalTag(null);
    setPortalPending(true);
    try {
      const { url } = await billingService.createPortalSession();
      window.location.assign(url);
    } catch (err) {
      setPortalTag(tagOf(err) ?? "generic");
    } finally {
      setPortalPending(false);
    }
  };

  const onDeleteAccount = async () => {
    setDeleteFailed(false);
    setDeleting(true);
    try {
      await accountService.deleteAccount();
      window.location.assign("/login");
    } catch {
      // Delete documents no tags — any failure gets the one generic message,
      // mirroring onManageBilling. The button re-enables so they can retry.
      setDeleteFailed(true);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="page-account" className="mx-auto max-w-4xl px-4 py-12">
        {t("common:actions.loading")}
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        data-testid="page-account"
        className="mx-auto max-w-4xl px-4 py-12 text-center"
      >
        <h1 className="mb-6 text-3xl font-semibold">{t("account.title")}</h1>
        <p role="alert" className="mb-4">
          {t("account.loadError")}
        </p>
        <button
          type="button"
          onClick={() => void reload()}
          className="btn"
        >
          {t("common:actions.retry")}
        </button>
      </div>
    );
  }

  // A user entitled through a parent's subscription has no Stripe customer
  // of their own — it is not their subscription to manage.
  const canManage = data.source === "self" && portalTag !== "no_customer";
  // Resume undoes the cancellation optimistically; treat it as no longer
  // pending the moment the call succeeds, before the webhook reconciles the DB.
  const cancelPending = data.cancelAtPeriodEnd && !resumed;
  // Matched by tier and interval, as on /pricing.
  const currentPlan =
    plans?.find(
      (plan) => plan.tier === data.tier && plan.interval === data.interval,
    ) ?? null;
  const sellsAddons =
    data.source === "self" && !!currentPlan && currentPlan.addonPrices.length > 0;
  const addonValue = addonTarget ?? data.addonQuantity;
  const reductionPending =
    pendingChange?.addonQuantity != null &&
    pendingChange.addonQuantity < data.addonQuantity;

  return (
    <div data-testid="page-account" className="mx-auto max-w-4xl px-4 py-12">
      <h1 className="mb-6 text-3xl font-semibold">{t("account.title")}</h1>

      <ProfileSettings />

      <div className="card mt-6 p-7">
        {data.tier === "free" ? (
          <>
            <p className="mb-6">{t("account.free")}</p>
            <Link to="/pricing" className="btn">
              {t("account.upgrade")}
            </Link>
          </>
        ) : (
          <div className="space-y-2">
            <p>
              <span className="text-ink-soft">{t("account.plan")}: </span>
              {KNOWN_TIERS.includes(data.tier)
                ? t(`tier.${data.tier}`)
                : data.tier}
            </p>
            <p>
              <span className="text-ink-soft">{t("account.status")}: </span>
              {t(`status.${data.status}`)}
            </p>
            {data.source === "parent" && <p>{t("source.parent")}</p>}
            {data.currentPeriodEnd && (
              <p>
                {t(
                  cancelPending ? "account.cancelsOn" : "account.renews",
                  {
                    date: format(
                      new Date(data.currentPeriodEnd),
                      "d MMM yyyy",
                      {
                        locale: i18n.resolvedLanguage === "zh" ? zhCN : enGB,
                      },
                    ),
                  },
                )}
              </p>
            )}
            <p>
              <span className="text-ink-soft">{t("account.usage")}: </span>
              {data.aiGenerationLimit === null
                ? `${data.aiGenerationUsed} · ${t("pricing.unlimited")}`
                : t("account.usageOf", {
                    used: data.aiGenerationUsed,
                    limit: data.aiGenerationLimit,
                  })}
            </p>
            {currentPlan && (
              <p data-testid="subjects-line">
                <span className="text-ink-soft">{t("account.subjects")}: </span>
                {currentPlan.includedSubjects === null
                  ? t("pricing.allSubjects")
                  : t("account.subjectsCount", {
                      included: currentPlan.includedSubjects,
                      extra: data.addonQuantity,
                    })}
              </p>
            )}

            {pendingChange?.planName && (
              <p
                data-testid="pending-change"
                aria-live="polite"
                className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200"
              >
                {pendingChange.effectiveAt
                  ? t("account.pendingChange", {
                      plan: pendingChange.planName,
                      date: format(
                        new Date(pendingChange.effectiveAt),
                        "d MMM yyyy",
                        {
                          locale:
                            i18n.resolvedLanguage === "zh" ? zhCN : enGB,
                        },
                      ),
                    })
                  : t("account.pendingChangeNoDate", {
                      plan: pendingChange.planName,
                    })}
              </p>
            )}

            {pendingChange?.addonQuantity != null &&
              (pendingChange.effectiveAt ?? data.currentPeriodEnd) && (
                <p
                  data-testid="pending-addons"
                  aria-live="polite"
                  className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200"
                >
                  {t("account.pendingAddons", {
                    count: pendingChange.addonQuantity,
                    date: fmtDate(
                      (pendingChange.effectiveAt ??
                        data.currentPeriodEnd) as string,
                    ),
                  })}
                </p>
              )}

            {sellsAddons && currentPlan && (
              <div className="space-y-3 rounded-2xl bg-sand p-4 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-display font-extrabold">
                    {t("account.extraSubjects")}
                  </span>
                  <AddonStepper
                    testId="addons-select"
                    value={addonValue}
                    max={Math.max(...currentPlan.addonPrices.map((p) => p.quantity))}
                    disabled={addonBusy}
                    onChange={(n) => {
                      setAddonTarget(n);
                      setAddonConfirm(null);
                    }}
                    label={t("account.extraSubjects")}
                    decreaseLabel={t("addons.fewer")}
                    increaseLabel={t("addons.more")}
                  />
                </div>

                {addonConfirm ? (
                  <div className="space-y-3 rounded-2xl border-[1.5px] border-hairline bg-surface p-3">
                    <p className="font-semibold">
                      {addonConfirm.kind === "increase"
                        ? t("account.addonsChargeNow", {
                            amount: formatAmount(
                              addonConfirm.amountDue,
                              addonConfirm.currency,
                              lang,
                            ),
                          })
                        : addonConfirm.kind === "keep"
                          ? t("account.addonsKeep")
                          : data.currentPeriodEnd
                            ? t("account.addonsReduce", {
                                date: fmtDate(data.currentPeriodEnd),
                              })
                            : t("account.addonsReduceNoDate")}
                    </p>
                    {/* The server releases any pending schedule on an
                      increase or a keep, which drops a scheduled plan change. */}
                    {pendingChange?.planName &&
                      addonConfirm.kind !== "decrease" && (
                        <p data-testid="addons-cancels-pending">
                          {t("account.addonsCancelsPending", {
                            plan: pendingChange.planName,
                          })}
                        </p>
                      )}
                    <div className="flex flex-wrap gap-3">
                      <button
                        type="button"
                        data-testid="addons-confirm"
                        disabled={addonBusy}
                        onClick={() => void onConfirmAddons(addonValue)}
                        className="btn-teal"
                      >
                        {t("account.confirmAddons")}
                      </button>
                      <button
                        type="button"
                        onClick={() => setAddonConfirm(null)}
                        className="btn-ghost"
                      >
                        {t("common:actions.cancel")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    data-testid="addons-review"
                    disabled={
                      addonBusy ||
                      (addonValue === data.addonQuantity && !reductionPending)
                    }
                    onClick={() =>
                      void onReviewAddons(addonValue, data.addonQuantity)
                    }
                    className="btn-ghost"
                  >
                    {t("account.updateAddons")}
                  </button>
                )}

                {addonDone && (
                  <p
                    data-testid="addons-done"
                    aria-live="polite"
                    className="text-teal-dark"
                  >
                    {t(
                      addonDone === "increase"
                        ? "account.addonsIncreased"
                        : addonDone === "keep"
                          ? "account.addonsKept"
                          : "account.addonsReduced",
                    )}
                  </p>
                )}
              </div>
            )}

            {data.source === "self" && plans && currentPlan && (
              <ChangePlan
                plans={plans}
                currentPlan={currentPlan}
                pendingChange={pendingChange}
                onChanged={() => void reload()}
              />
            )}

            {(portalTag || resumeTag || addonTag) && (
              <p
                role="alert"
                className="rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200"
              >
                {(() => {
                  const tag = (portalTag ?? resumeTag ?? addonTag) as string;
                  return KNOWN_TAGS.includes(tag)
                    ? t(`errors.${tag}`)
                    : t("common:errors.generic");
                })()}
              </p>
            )}

            <div className="flex flex-wrap gap-3">
              {canManage && (
                <button
                  type="button"
                  disabled={portalPending}
                  onClick={() => void onManageBilling()}
                  className="btn"
                >
                  {t("account.manage")}
                </button>
              )}
              {canManage && cancelPending && (
                <button
                  type="button"
                  data-testid="resume-subscription"
                  disabled={resuming}
                  onClick={() => void onResume()}
                  className="btn-teal"
                >
                  {t("account.resume")}
                </button>
              )}
            </div>
            {/* No Upgrade link here: this branch only renders when
              data.tier !== "free" (the free case is handled above), so a
              paid tier hitting no_customer must never be invited to
              "upgrade" to the plan it already has. */}
          </div>
        )}
      </div>

      <section className="card mt-6 p-7">
        <h2 className="text-xl font-semibold text-red-600 dark:text-red-400">
          {t("account.delete.title")}
        </h2>
        <p className="mt-2 text-sm text-ink-soft">
          {t("account.delete.warning")}
        </p>
        {deleteFailed && (
          <p
            role="alert"
            className="mt-4 rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200"
          >
            {t("common:errors.generic")}
          </p>
        )}
        {!confirmingDelete ? (
          <button
            type="button"
            onClick={() => setConfirmingDelete(true)}
            className="btn-ghost border-red-500 text-red-600 dark:text-red-400"
          >
            {t("account.delete.cta")}
          </button>
        ) : (
          <div className="mt-4 flex gap-3">
            <button
              type="button"
              disabled={deleting}
              onClick={() => void onDeleteAccount()}
              className="rounded-full bg-red-600 px-5 py-2.5 font-display font-extrabold text-white disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
            >
              {t("account.delete.confirm")}
            </button>
            <button
              type="button"
              onClick={() => setConfirmingDelete(false)}
              className="btn-ghost"
            >
              {t("common:actions.cancel")}
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default Account;
